import React, { useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';

const StrategySection = ({ project }) => {
  const [activeTab, setActiveTab] = useState('pitch');

  // Hardcoded strategy data for demo (should come from project.content later)
  const strategy = {
    pitch: project.content?.pitch || "Vår løsning hjelper dere å redusere kostnader med 20% ved å automatisere...",
    competitors: [
      { name: "Konkurrent A", type: "Prispresser", weakness: "Dårlig support", strategy: "Fokusér på vår 24/7 lokale support og ROI-garanti." },
      { name: "Gjøre ingenting", type: "Status Quo", weakness: "Ineffektivitet", strategy: "Vis kostnaden ved å vente (Cost of Delay kalkyle)." }
    ],
    winThemes: [
      "Lokal tilstedeværelse", "Raskeste implementeringstiden", "Sikkerhet (ISO-sertifisert)"
    ],
    objections: [
      { q: "Det er for dyrt", a: "Fokusér på totalverdi over 3 år, ikke oppstartskostnad. Bruk ROI-kalkulatoren." },
      { q: "Vi har ikke tid nå", a: "Vi tar hele migreringsjobben. Deres team trenger kun sette av 2 timer." }
    ]
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      
      {/* Left Menu */}
      <div className="lg:col-span-3 space-y-2">
        <button onClick={() => setActiveTab('pitch')} className={`w-full text-left p-4 rounded-xl font-bold text-sm flex items-center gap-3 transition-all ${activeTab === 'pitch' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
          <SafeIcon icon="🎤" /> Salgspitch
        </button>
        <button onClick={() => setActiveTab('competitors')} className={`w-full text-left p-4 rounded-xl font-bold text-sm flex items-center gap-3 transition-all ${activeTab === 'competitors' ? 'bg-red-600 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
          <SafeIcon icon="⚔️" /> Konkurrenter
        </button>
        <button onClick={() => setActiveTab('objections')} className={`w-full text-left p-4 rounded-xl font-bold text-sm flex items-center gap-3 transition-all ${activeTab === 'objections' ? 'bg-amber-500 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
          <SafeIcon icon="🛡️" /> Innvendinger
        </button>
      </div>

      {/* Main Content Area */}
      <div className="lg:col-span-9 space-y-6">
        
        {activeTab === 'pitch' && (
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm animate-in fade-in slide-in-from-right-4">
            <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
              <SafeIcon icon="🎤" className="text-primary-600" /> Hovedbudskap & Pitch
            </h3>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 mb-6">
              <p className="text-lg font-medium text-slate-700 italic leading-relaxed">"{strategy.pitch}"</p>
            </div>
            
            <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4">Våre Vinner-temaer (Win Themes)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {strategy.winThemes.map((theme, i) => (
                <div key={i} className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3">
                  <SafeIcon icon="✅" className="text-emerald-600" />
                  <span className="font-bold text-emerald-900 text-sm">{theme}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'competitors' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
            <div className="bg-red-50 p-6 rounded-2xl border border-red-100 flex items-start gap-4">
              <div className="p-3 bg-white rounded-xl text-red-600 shadow-sm"><SafeIcon icon="⚠️" className="w-6 h-6" /></div>
              <div>
                <h3 className="font-black text-red-900 text-lg">Konkurrentbilde</h3>
                <p className="text-red-700 font-medium">Hvem andre kjemper om dette prosjektet?</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {strategy.competitors.map((comp, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <h4 className="text-lg font-black text-slate-900">{comp.name}</h4>
                    <span className="inline-block px-2 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-widest rounded mt-2">{comp.type}</span>
                  </div>
                  <div className="md:w-2/3 space-y-3">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Svakhet</p>
                      <p className="text-sm font-bold text-slate-700">{comp.weakness}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">Vår Mot-strategi</p>
                      <p className="text-sm font-medium text-blue-900">{comp.strategy}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'objections' && (
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm animate-in fade-in slide-in-from-right-4">
            <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
              <SafeIcon icon="🛡️" className="text-amber-500" /> Håndtering av Innvendinger
            </h3>
            <div className="space-y-4">
              {strategy.objections.map((obj, i) => (
                <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-slate-50 p-4 font-bold text-slate-800 border-b border-slate-200 flex gap-3">
                    <span className="text-amber-500">Q:</span> {obj.q}
                  </div>
                  <div className="p-5 bg-white text-slate-600 font-medium leading-relaxed">
                    <span className="text-emerald-600 font-bold mr-2">Svar:</span> {obj.a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StrategySection;